<style lang="scss" scoped>
@include component(match) {
	@include part(header) {
		@include part(steps) {
			margin-top: 20px;
		}
	}
	@include part(description) {
		padding: 0;
		margin: 0;
	}
}
.main-panel__footer {
	justify-content: end;
}
</style>
<template lang="pug">
.match
	app-header
		.match__header(slot="header")
			h1 Connect to {{ providerFullName }}
			.match__header__steps
				el-steps(:active="2", finish-status="success")
					el-step(title="Connect")
					el-step(title="Settings")
					el-step(title="Match")
					el-step(title="Classes")
	.container
		.main-panel.directory
			loading(v-if="$apollo.loading || predictionsLoading")
			template(v-else="")
				.main-panel__padding
					p.match__description 
						| Below are predicted matches for the data stored in Edusync compared to the users already in your {{ providerName }} account.
						| Please review these, and click <b>Save</b> once you are happy that you have ignored any incorrect matches.
						| You will be able to work with ignored matches once setup is complete.
				el-table.custom-table.border-top(v-if="!predictionsEmpty", :row-class-name="tableRowClassName", :data="predictions", style="width: 100%")
					el-table-column(width="200" prop="WondeUser.last_name", sortable="", label="Edusync user")
						template(slot-scope="scope")
							| {{ scope.row.WondeUser.first_name }} {{ scope.row.WondeUser.last_name }}
					el-table-column(width="180" prop="ThirdPartyUser.last_name", sortable="", :label="`${providerName} user`")
						template(slot-scope="scope")
							| {{ scope.row.ThirdPartyUser.first_name }} {{ scope.row.ThirdPartyUser.last_name }}
					el-table-column(prop="ThirdPartyUser.email", sortable="", :label="`${providerName} email`")
					el-table-column(width="140", prop="type", label="Date of Birth")
						template(slot-scope="scope")
							| {{ moment(parseInt(scope.row.WondeUser.date_of_birth)).format("DD-MM-YYYY") }}
					el-table-column(width="120", prop="type", label="Type")
						template(slot-scope="scope")
							| {{ scope.row.WondeUser.base_type | capitalize }}
					el-table-column(width="200" prop="score", sortable="", label="Prediction strength")
						template(slot-scope="scope")
							template(v-if="scope.row.skip")
								el-progress(v-if="scope.row.rank === 'confident'", :percentage="100", status="exception")
								el-progress(v-if="scope.row.rank === 'likely'", :percentage="80", status="exception")
								el-progress(v-if="scope.row.rank === 'weak'", :percentage="40", status="exception")
							template(v-else="")
								el-progress(v-if="scope.row.rank === 'confident'", :percentage="100", status="success")
								el-progress(v-if="scope.row.rank === 'likely'", :percentage="80", status="success")
								el-progress(v-if="scope.row.rank === 'weak'", :percentage="40", status="exception")
					el-table-column(width="150", align="center", label="Action")
						template(slot-scope="scope")
							el-button(:type="scope.row.skip ? 'danger' : 'secondary'", @click="skip(scope.row)", size="small") {{ scope.row.skip ? 'Ignored' : 'Ignore' }}
				.main-panel__footer.d-flex.align-items-center
					el-button(type="primary", @click="refetchUsers") Refetch users
					el-button.mega-action(@click="skipAll") Ignore all
					el-button.mega-action(type="success", @click="saveByBatch", :loading="loading") {{!predictionsEmpty ? 'Match' : 'Skip'}}
</template>
<script>
import _ from "lodash";
import moment from 'moment'
import gql from "graphql-tag";
import Bugsnag from '@bugsnag/js';
export default {
	data() {
		return {
			moment,
			loading: false,
		};
	},
	computed: {
		predictionsLoading() {
			return _.isEmpty(this.predictions);
		},
		predictionsEmpty() {
			return this.predictions.length === 1 && !this.predictions[0].WondeUser && !this.predictions[0].ThirdPartyUser;
		}
	},
	apollo: {
		predictions: {
			query: gql`
				{
					Predictions {
						WondeUser {
							id
							first_name
							last_name
							date_of_birth
							email
							base_type
						}
						ThirdPartyUser {
							id
							first_name
							last_name
							email
						}
						rank
					}
				}
			`,
			fetchPolicy: "no-cache",
			update(r) {
				if (r || r.Predictions || r.Predictions.length) {
					let data = _.cloneDeep(r.Predictions);
					data = _.map(data, (i) => {
						switch (i.rank) {
							case "confident":
								i.score = 1;
								i.skip = false;
								break;
							case "likely":
								i.score = 2;
								i.skip = false;
								break;
							case "weak":
								i.score = 3;
								i.skip = true;
								break;
							default:
								i.skip = true;
								i.score = 4;
								break;
						}
						return i;
					});
					return data
				}
			},
			pollInterval: 5000,
			skip() {
				return !this.predictionsLoading;
			}
		},
	},
	methods: {
		async save(predictions) {
			try {
				await this.$apollo.mutate({
					mutation: gql`
						mutation SavePredictions($input: [PredictionsInput!]!) {
							SavePredictions(input: $input) {
								id
								has_completed_wizard
							}
						}
					`,
					variables: {
						input: _.map(predictions, (r) => {
							return {
								idpId: r.WondeUser.id,
								spId: r.ThirdPartyUser.id,
								action: r.skip == true ? "unmatch" : "match",
							};
						}),
					},
				});
			} catch (e) {
				Bugsnag.notify(JSON.stringify(e, null, 2));
				this.$message.error("Failed to update, please contact support if issue continues");
			}
		},
		async saveByBatch() {
			this.loading = true;
			const perPage = 50;
			let curPage = 1;
			let promises = [];

			if(!this.predictionsEmpty) {
				while(_.chunk(this.predictions, perPage)[curPage - 1]) {
					promises.push(this.save(_.chunk(this.predictions, perPage)[curPage - 1]));
					curPage++;
				}
				await Promise.all(promises);
			}

			this.loading = false;
			this.$router.push({ name: "wizard.classes" });
		},
		skipWeak() {
			_.each(this.predictions, (r) => {
				if (r.rank === "weak") {
					this.$set(r, "skip", true);
				}
			});
		},
		skipAll() {
			_.each(this.predictions, (r) => {
				this.$set(r, "skip", true);
			});
		},
		tableRowClassName({ row }) {
			return row.skip ? "skipped" : "included";
		},
		skip(row) {
			this.$set(row, "skip", !row.skip);
		},
		async refetchUsers() {
			this.$confirm(
					`Re-fetching users will take some time. Are you sure you want to proceed?`,
					"Warning",
					{
						confirmButtonText: "OK",
						cancelButtonText: "Cancel",
						type: "warning"
					}
			).then(async () => {
				try {
					await this.$apollo.mutate({
						mutation: gql`
							mutation CacheClear($item: CacheTypeEnum!) {
								CacheClear(item: $item)
							}`,
							variables: {
								item: "users"
							}
					});
					this.$apollo.queries.predictions.skip = false
    				await this.$apollo.queries.predictions.refetch()
				} catch (e) {
					this.$message.error("Failed to re-fetch users");
				}
			})
		}
	},
};
</script>
